import { useQuery } from 'react-query';
import wireTransfers from 'services/api/wireTransfers';
import useTranslation from 'utils/hooks/useTranslation';
import notify from 'utils/notificationUtils';
import useAmplitude from 'utils/hooks/useAmplitude';
import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import { DEFAULT_EXPORT_PAGE_SIZE } from 'constants/tables';
import { saveProofOfPaymentFile, isProofValidated } from 'pages/FundingRequests/helper';
import { wireTransferQueryKeys } from './queryKeys';

const { getWireTransfersQuery, downloadWireTransferProofQuery } = wireTransferQueryKeys;

export const useGetWireTransfers = ({
  pageNumber,
  pageSize,
  status,
  orderBy,
  filter,
  enabled = true,
}) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();

  const getWireTransfers = async () => {
    const result = await wireTransfers.read({ pageNumber, pageSize, status, orderBy, filter });
    // Axios body is the Wallet Service envelope: { data: WireTransferList }
    return result?.data?.data;
  };

  return useQuery({
    queryKey: [getWireTransfersQuery, { pageNumber, pageSize, status, orderBy, filter }],
    queryFn: getWireTransfers,
    enabled,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    // Lets the pending-count query (badge + page) reuse a just-fetched result
    // instead of re-pulling the full pending queue on every remount.
    staleTime: 10000,
    onError: () => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.wireTransfers.get.error'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.wireTransfersFetchFailed);
    },
  });
};

// The API has no way to filter by proof status, so this over-fetches the pending
// queue and counts client-side the ones actually ready for review (proof validated).
export const useGetPendingFundingRequestsCount = ({ enabled = true } = {}) => {
  const { data, ...rest } = useGetWireTransfers({
    pageNumber: 1,
    pageSize: DEFAULT_EXPORT_PAGE_SIZE,
    status: 'pending',
    enabled,
  });

  const pendingCount = (data?.items || []).filter(item => isProofValidated(item.proofStatus))
    .length;

  return { ...rest, pendingCount };
};

// `company` is the request's own corporation, for analytics
export const useDownloadWireTransferProof = ({ wireTransferId, fileName, company }) => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();

  const downloadProof = async () => {
    const result = await wireTransfers.downloadProof(wireTransferId);
    return result?.data;
  };

  return useQuery({
    queryKey: [downloadWireTransferProofQuery, wireTransferId],
    queryFn: downloadProof,
    enabled: !!wireTransferId,
    retry: false,
    cacheTime: 0, // Don't cache downloads
    staleTime: 0, // Always fetch fresh
    keepPreviousData: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    onSuccess: data => {
      saveProofOfPaymentFile(data, fileName);
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.fundingRequestProofOfPaymentViewed, {
        company,
        requestId: wireTransferId,
      });
    },
    onError: () => {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.fundingRequests.proof.downloadError'),
      });
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.fundingRequestProofDownloadFailed);
    },
  });
};

export default {
  useGetWireTransfers,
  useGetPendingFundingRequestsCount,
  useDownloadWireTransferProof,
};
